// src/components/features/departures/LiveDeparturesTab/LiveDeparturesTab.tsx
import { useLiveDeparturesSearch } from '../../../../data/useLiveDepartures';
import {
  getDeparturesStopAtcoCode,
  type JourneyWidgetPage,
} from '../../../widget/pageState';
import { DeparturesResultsPage } from '../ResultsPage/DeparturesResultsPage';
import { DeparturesSearchPage } from '../SearchPage/DeparturesSearchPage';
import { StopDeparturesDetailsPage } from '../StopDetailsPage/StopDeparturesDetailsPage';
import { useEffect, useRef } from 'preact/hooks';
import type { PlaceOption } from '../../../../data/places';
import { useWidgetAnalytics } from '../../../../analytics/useWidgetAnalytics';
// styles
import styles from '../../../widget/Widget.module.css';

interface LiveDeparturesTabProps {
  currentPage: JourneyWidgetPage;
  initialDepartureStopId?: string;
  onShowResults: () => void;
  onShowDetails: (detailsId: string) => void;
}

export function LiveDeparturesTab({
  currentPage,
  initialDepartureStopId = '',
  onShowResults,
  onShowDetails,
}: LiveDeparturesTabProps) {
  const { track } = useWidgetAnalytics();
  const hasAutoOpenedInitialStopRef = useRef(false);
  const nearbyStopsWasLoadingRef = useRef(false);
  const trimmedInitialDepartureStopId = initialDepartureStopId.trim();
  const selectedStopAtcoCode = getDeparturesStopAtcoCode(currentPage);

  const {
    locationQuery,
    setLocationQuery,
    locationOptions,
    handleSubmit,
    handleUseCurrentLocation,
    submitError,
    searchLocationsError,
    mapState,
  } = useLiveDeparturesSearch({
    initialDepartureStopId,
    selectedStopAtcoCode,
  });

  const isSearchPage = currentPage.kind === 'search';
  const isResultsPage = currentPage.kind === 'results';
  const isDetailsPage = currentPage.kind === 'details';

  const selectedNearbyStop =
    selectedStopAtcoCode === null
      ? null
      : (mapState.nearbyStops.find(
          (stop) => stop.atcoCode === selectedStopAtcoCode,
        ) ?? null);

  useEffect(() => {
    if (!trimmedInitialDepartureStopId) {
      return;
    }

    if (hasAutoOpenedInitialStopRef.current) {
      return;
    }

    if (currentPage.kind !== 'search') {
      return;
    }

    hasAutoOpenedInitialStopRef.current = true;
    onShowResults();
    onShowDetails(trimmedInitialDepartureStopId);
  }, [
    currentPage.kind,
    onShowDetails,
    onShowResults,
    trimmedInitialDepartureStopId,
  ]);

  useEffect(() => {
    if (!isResultsPage) {
      nearbyStopsWasLoadingRef.current = false;
      return;
    }
    if (mapState.nearbyStopsLoading) {
      nearbyStopsWasLoadingRef.current = true;
      return;
    }
    if (!nearbyStopsWasLoadingRef.current) return;
    nearbyStopsWasLoadingRef.current = false;

    track({
      name: 'departures_results_loaded',
      activity: 'departures',
      page: 'results',
      payload: {
        status: mapState.nearbyStopsError
          ? 'error'
          : mapState.nearbyStops.length === 0
            ? 'empty'
            : 'success',
        selectedLocationId: mapState.selectedLocation?.id ?? null,
        selectedLocationType: mapState.selectedLocation?.type ?? 'unknown',
        stopCount: mapState.nearbyStops.length,
        ...(mapState.nearbyStopsError
          ? { errorMessage: mapState.nearbyStopsError.message }
          : {}),
      },
    });
  }, [
    isResultsPage,
    mapState.nearbyStopsLoading,
    mapState.nearbyStops,
    mapState.nearbyStopsError,
    mapState.selectedLocation,
    track,
  ]);

  const handleSubmitLocation = (
    event?: SubmitEvent,
    selection?: PlaceOption,
  ) => {
    const didSubmit = handleSubmit(event, selection);

    if (didSubmit) {
      track({
        name: 'departures_search_submitted',
        activity: 'departures',
        page: 'search',
        payload: {
          locationQuery,
          selectedLocationType: selection?.type ?? 'unknown',
        },
      });
      onShowResults();
    }
  };

  const handleUseCurrentLocationSelection = async () => {
    const didSelect = await handleUseCurrentLocation();

    if (didSelect) {
      onShowResults();
    }
  };

  const handleShowDetails = (detailsId: string) => {
    track({
      name: 'departures_stop_selected',
      activity: 'departures',
      page: 'results',
      payload: { atcoCode: detailsId },
    });
    onShowDetails(detailsId);
  };

  return (
    <div className={styles.tabWrapper}>
      <div className={styles.tabPage}>
        {isSearchPage && (
          <DeparturesSearchPage
            locationQuery={locationQuery}
            setLocationQuery={setLocationQuery}
            locationOptions={locationOptions}
            onSubmit={handleSubmitLocation}
            onUseCurrentLocation={handleUseCurrentLocationSelection}
            searchLocationsError={searchLocationsError}
            submitError={submitError}
          />
        )}

        {isResultsPage && (
          <DeparturesResultsPage
            locationQuery={locationQuery}
            setLocationQuery={setLocationQuery}
            locationOptions={locationOptions}
            onSubmit={handleSubmitLocation}
            onUseCurrentLocation={handleUseCurrentLocationSelection}
            mapState={mapState}
            onShowDetails={handleShowDetails}
          />
        )}

        {isDetailsPage && (
          <StopDeparturesDetailsPage
            selectedStopAtcoCode={selectedStopAtcoCode}
            fallbackStop={selectedNearbyStop}
            fallbackStopName={selectedNearbyStop?.commonName ?? null}
          />
        )}
      </div>
    </div>
  );
}
